import React, { FC, memo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import CustomInputWithLabel from '../../common/CustomInputWithLabel';
import LabelWrapper from '../../common/LabelWrapper';
import CustomInput from '../../custom/CustomInput';
import BoxComponent from '../../custom/BoxComponent';
import { PlotArea } from '../../../models/inputTypes/PmsFields';
import CustomCheckbox from '../../custom/CustomCheckbox';

const styles = {
  spacingContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  alignEndContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  },
  largeChildSpacing: {
    '& >:nth-child(n)': {
      my: 1,
      mr: { xs: 4, md: 6, lg: 8 },
    },
  },
  smallChildSpacing: {
    '& >:nth-child(n)': {
      my: 3,
      mr: 4,
    },
  },

  smallVariableSpacing: {
    '& >:nth-child(n)': {
      my: 1,
      mr: 4,
    },
    '& >:first-child': {
      my: 1,
      mr: 2,
    },
  },
  coordinates: {
    '& >:nth-child(2)': {
      ml: 1,
    },
  },
} as const;

type PmsPlotAreasInputsProps = {
  inputState: PlotArea;
  setInputState: (value: PlotArea) => void;
};

const PmsPlotAreasInputsGroup: FC<PmsPlotAreasInputsProps> = ({ inputState, setInputState }) => {
  const [valueX, setValueX] = useState(1);
  const [valueY, setValueY] = useState(1);
  const onCheckboxClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    const currentValue: 'h' | 'z' | 'dir' | 'c' | 'rad' | 'init_depths' = e.target.id;
    const newValue = inputState[currentValue] ? 0 : 1;
    setInputState({ ...inputState, [currentValue]: newValue });
  };

  const onInputChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setInputState({ ...inputState, [event.target.name]: event.target.value });
  };

  const onNumberChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setInputState({ ...inputState, [event.target.name]: event.target.value });
  };

  const onInputChangeX = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setValueX(+event.target.value);
    setInputState({ ...inputState, [event.target.name]: event.target.value });
  };
  const onInputChangeY = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setValueY(+event.target.value);
    setInputState({ ...inputState, [event.target.name]: event.target.value });
  };

  return (
    <Box>
      <Typography mb={1} variant={'subtitle1'}>
        Bounding Points & Grid Cell Node Numbers
      </Typography>
      <Box mb={2} sx={{ ...styles.spacingContainer, ...styles.largeChildSpacing }}>
        <Box>
          <LabelWrapper label={'Southwestern point'}>
            <Box sx={{ display: 'flex', ...styles.coordinates }}>
              <CustomInputWithLabel
                min={1}
                step={1}
                required
                errorText={'Enter positive integer'}
                type={'number'}
                onChange={onNumberChange}
                value={inputState.sw_x}
                name={'sw_x'}
                label={'X'}
              />
              <CustomInputWithLabel
                min={1}
                step={1}
                required
                errorText={'Enter positive integer'}
                type={'number'}
                onChange={onNumberChange}
                value={inputState.sw_y}
                name={'sw_y'}
                label={'Y'}
              />
            </Box>
          </LabelWrapper>
        </Box>
        <Box>
          <LabelWrapper label={'Northeastern point'}>
            <Box sx={{ display: 'flex', ...styles.coordinates }}>
              <CustomInputWithLabel
                min={inputState.sw_x}
                step={1}
                required
                errorText={
                  valueX < inputState.sw_x && valueX > 0
                    ? 'Number should be >= than first point'
                    : 'Enter positive integer'
                }
                type={'number'}
                onChange={onInputChangeX}
                value={inputState.ne_x}
                name={'ne_x'}
                label={'X'}
              />
              <CustomInputWithLabel
                min={inputState.sw_y}
                step={1}
                required
                errorText={
                  valueY < inputState.sw_y && valueY > 0
                    ? 'Number should be >= than first point'
                    : 'Enter positive integer'
                }
                type={'number'}
                onChange={onInputChangeY}
                value={inputState.ne_y}
                name={'ne_y'}
                label={'Y'}
              />
            </Box>
          </LabelWrapper>
        </Box>
      </Box>
      <Typography mx={1} variant={'subtitle1'}>
        Variables
      </Typography>
      <Box sx={{ ...styles.smallChildSpacing }}>
        <Box sx={{ ...styles.alignEndContainer }}>
          <Box mr={2}>
            <CustomCheckbox checked={!!inputState.h} id={'h'} onClick={onCheckboxClick} />
          </Box>
          <BoxComponent disabled={!inputState.h}>
            <LabelWrapper label={'Significant Wave height (m)'}>
              <CustomInput
                minWidth={'160px'}
                disabled={!inputState.h}
                required
                errorText={'Please enter file name'}
                maxLength={30}
                onChange={onInputChange}
                value={inputState.name_h}
                name={'name_h'}
              />
            </LabelWrapper>
          </BoxComponent>
        </Box>
        <Box sx={{ ...styles.alignEndContainer }}>
          <Box mr={2}>
            <CustomCheckbox checked={!!inputState.c} id={'c'} onClick={onCheckboxClick} />
          </Box>
          <BoxComponent disabled={!inputState.c}>
            <LabelWrapper label={'Wave Celerity (m/s)'}>
              <CustomInput
                minWidth={'160px'}
                disabled={!inputState.c}
                required
                errorText={'Please enter file name'}
                type={'text'}
                maxLength={30}
                onChange={onInputChange}
                value={inputState.name_c}
                name={'name_c'}
              />
            </LabelWrapper>
          </BoxComponent>
        </Box>
        <Box sx={{ ...styles.alignEndContainer }}>
          <Box mr={2}>
            <CustomCheckbox checked={!!inputState.z} id={'z'} onClick={onCheckboxClick} />
          </Box>
          <BoxComponent disabled={!inputState.z}>
            <LabelWrapper label={'Surface elevation (m)'}>
              <CustomInput
                minWidth={'160px'}
                disabled={!inputState.z}
                required
                errorText={'Please enter file name'}
                type={'text'}
                maxLength={30}
                onChange={onInputChange}
                value={inputState.name_z}
                name={'name_z'}
              />
            </LabelWrapper>
          </BoxComponent>
        </Box>
        <Box sx={{ ...styles.alignEndContainer }}>
          <Box mr={2}>
            <CustomCheckbox checked={!!inputState.dir} id={'dir'} onClick={onCheckboxClick} />
          </Box>
          <BoxComponent disabled={!inputState.dir}>
            <LabelWrapper label={'Wave direction (deg)'}>
              <CustomInput
                minWidth={'160px'}
                disabled={!inputState.dir}
                required
                errorText={'Please enter file name'}
                type={'text'}
                maxLength={30}
                onChange={onInputChange}
                value={inputState.name_dir}
                name={'name_dir'}
              />
            </LabelWrapper>
          </BoxComponent>
        </Box>
        <Box sx={{ ...styles.alignEndContainer, ...styles.smallVariableSpacing }}>
          <Box mr={2}>
            <CustomCheckbox checked={!!inputState.rad} id={'rad'} onClick={onCheckboxClick} />
          </Box>
          <BoxComponent disabled={!inputState.rad}>
            <LabelWrapper label={'Radiation stresses (m\u00B2/s\u00B2)'}>
              <CustomInput
                disabled={!inputState.rad}
                required
                minWidth={'160px'}
                type={'text'}
                errorText={'Please enter file name'}
                maxLength={30}
                onChange={onInputChange}
                value={inputState.name_rad_xx}
                name={'name_rad_xx'}
              />
            </LabelWrapper>
          </BoxComponent>

          <BoxComponent disabled={!inputState.rad}>
            <CustomInput
              disabled={!inputState.rad}
              type={'text'}
              minWidth={'160px'}
              errorText={'Please enter file name'}
              maxLength={30}
              onChange={onInputChange}
              value={inputState.name_rad_xy}
              name={'name_rad_xy'}
            />
          </BoxComponent>
          <BoxComponent disabled={!inputState.rad}>
            <CustomInput
              disabled={!inputState.rad}
              type={'text'}
              minWidth={'160px'}
              errorText={'Please enter file name'}
              maxLength={30}
              onChange={onInputChange}
              value={inputState.name_rad_yy}
              name={'name_rad_yy'}
            />
          </BoxComponent>
        </Box>
        <Box sx={{ ...styles.alignEndContainer }}>
          <Box mb={1} mr={2}>
            <CustomCheckbox checked={!!inputState.init_depths} id={'init_depths'} onClick={onCheckboxClick} />
          </Box>
          <BoxComponent disabled={!inputState.init_depths}>
            <LabelWrapper label={'Initial Depths (m)'}>
              <CustomInput
                required
                disabled={!inputState.init_depths}
                type={'text'}
                minWidth={'160px'}
                errorText={'Please enter file name'}
                maxLength={30}
                onChange={onInputChange}
                value={inputState.name_init_depths}
                name={'name_init_depths'}
              />
            </LabelWrapper>
          </BoxComponent>
        </Box>
      </Box>
    </Box>
  );
};

export default memo(PmsPlotAreasInputsGroup);
